/**
 * AudioManager - Web Audio API Synthesis Engine
 * Encapsulates procedural audio synthesis, sound effects (card snaps,
 * resonant wooden peg strikes, textured shuffles, alarms, fanfares),
 * audio context lifecycle management, and mute/volume settings.
 */

import { appStorage } from '../storage/IndexedDBStorage.js';

export class AudioManager {
  constructor(options = {}) {
    this.on = true;
    this.ctx = null;
    this.storage = options.storage || null;
    if (typeof options.on === 'boolean') {
      this.on = options.on;
    } else {
      const s = this.storage || (typeof window !== 'undefined' && window.appStorage) || appStorage || (typeof localStorage !== 'undefined' ? localStorage : null);
      if (s) {
        try {
          this.on = s.getItem('crazyCribbage.sfx') !== 'off';
        } catch (e) {}
      }
    }
  }

  /**
   * Lazily initializes or resumes the AudioContext upon user gesture.
   * @returns {AudioContext|null}
   */
  ensure() {
    if (!this.ctx && typeof window !== 'undefined') {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      this.ctx = new AC();
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }
    return this.ctx;
  }

  /**
   * Synthesizes an oscillator tone with envelope shaping and optional frequency slide.
   * @param {number} freq Frequency in Hz
   * @param {number} dur Duration in seconds
   * @param {OscillatorType} [type='triangle'] Waveform
   * @param {number} [gain=0.08] Peak gain
   * @param {number} [slide=null] Target slide frequency
   */
  tone(freq, dur, type = 'triangle', gain = 0.08, slide = null) {
    if (!this.on) return;
    const ctx = this.ensure();
    if (!ctx) return;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, ctx.currentTime);
    if (slide) {
      o.frequency.exponentialRampToValueAtTime(Math.max(20, slide), ctx.currentTime + dur);
    }
    g.gain.setValueAtTime(gain, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
    o.connect(g);
    g.connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + dur);
  }

  /**
   * Synthesizes shaped white noise with biquad filtering.
   * @param {number} dur Duration in seconds
   * @param {number} [gain=0.05] Peak gain
   * @param {number} [filterFreq=null] Cutoff frequency
   * @param {BiquadFilterType} [filterType='lowpass'] Filter mode
   */
  noise(dur, gain = 0.05, filterFreq = null, filterType = 'lowpass') {
    if (!this.on) return;
    const ctx = this.ensure();
    if (!ctx) return;
    const bufferSize = Math.max(1, Math.floor(ctx.sampleRate * dur));
    const buf = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
    }
    const n = ctx.createBufferSource();
    n.buffer = buf;
    const g = ctx.createGain();
    g.gain.setValueAtTime(gain, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);

    if (filterFreq) {
      const f = ctx.createBiquadFilter();
      f.type = filterType;
      f.frequency.value = filterFreq;
      n.connect(f);
      f.connect(g);
    } else {
      n.connect(g);
    }
    g.connect(ctx.destination);
    n.start();
  }

  /**
   * Crisp card snap / table drop sound.
   */
  snap() {
    this.noise(0.04, 0.06, 3200, 'bandpass');
    this.tone(210, 0.05, 'triangle', 0.04, 90);
  }

  /**
   * Real wooden peg "thunk" sound when pegs advance on the hardwood board.
   * Synthesizes an initial sharp wooden impact transient, followed by a resonant
   * body chamber decay and subtle hole air/friction pop.
   * @param {number} [pitchMod=1.0] Optional pitch multiplier
   */
  thunk(pitchMod = 1.0) {
    if (!this.on) return;
    const variance = (Math.random() * 0.14 - 0.07);
    const mod = Math.max(0.65, Math.min(1.5, pitchMod + variance));

    // 1. High wooden contact tap chirp (1750Hz -> 850Hz)
    const tapFreq = 1750 * mod;
    this.tone(tapFreq, 0.016, 'triangle', 0.10, tapFreq * 0.48);

    // 2. Solid deep wood body "thunk" resonance (220Hz -> 140Hz)
    const bodyFreq = 220 * mod;
    this.tone(bodyFreq, 0.065, 'sine', 0.12, bodyFreq * 0.65);

    // 3. Resonant cavity undertone
    this.tone(bodyFreq * 1.5, 0.04, 'triangle', 0.05, bodyFreq * 0.8);

    // 4. Subtle air/friction pop in drilled hole
    this.noise(0.018, 0.04, 750 * mod, 'lowpass');
  }

  /**
   * Wooden peg insertion strike with micro-pitch randomization and resonant cavity.
   */
  peg() {
    this.thunk(1.0);
  }

  /**
   * Authentic micro-pitch wooden peg tactile clack sound for hole-by-hole movement.
   * @param {number} [pitchMod=1.0] Optional pitch multiplier
   */
  clack(pitchMod = 1.0) {
    this.thunk(pitchMod);
  }

  /**
   * Party popper pneumatic burst sound for confetti cannons.
   */
  confettiPop() {
    if (!this.on) return;
    this.tone(180, 0.06, 'triangle', 0.12, 45);
    this.noise(0.08, 0.08, 1400, 'lowpass');
    setTimeout(() => {
      this.noise(0.12, 0.04, 3400, 'highpass');
    }, 25);
  }

  /**
   * Crisp whispering card stock friction slide on the deal.
   * Synthesizes card stock sliding across felt/wood with a frequency sweep
   * followed by light card contact.
   */
  cardSlide() {
    if (!this.on) return;
    const variance = (Math.random() * 0.12 - 0.06);

    // Whispering cardstock friction sweep
    const slideFreq = 2700 * (1 + variance);
    this.noise(0.065, 0.045, slideFreq, 'bandpass');

    // Light card landing contact
    setTimeout(() => {
      this.tone(190 * (1 + variance), 0.035, 'triangle', 0.04, 95);
      this.noise(0.02, 0.03, 3400, 'highpass');
    }, 38);
  }

  /**
   * Textured multi-card shuffle with realistic card interleaving flutter and bridge snap.
   */
  shuffle() {
    if (!this.on) return;
    // Initial riffle sweep immediate
    this.noise(0.08, 0.04, 1400 + Math.random() * 900, 'bandpass');
    // Remaining riffle sweeps
    const swishes = [60, 120, 185, 250, 315, 380];
    swishes.forEach((delay, i) => {
      setTimeout(() => {
        const freq = 1400 + Math.random() * 900;
        this.noise(0.08, 0.04 - (i + 1) * 0.003, freq, 'bandpass');
      }, delay);
    });

    // Cascading card interleaving clicks
    for (let c = 0; c < 12; c++) {
      setTimeout(() => {
        this.noise(0.015, 0.025, 2600 + Math.random() * 1200, 'highpass');
      }, 70 + c * 24);
    }

    // Bridge gathering snap
    setTimeout(() => {
      this.snap();
      this.tone(260, 0.06, 'triangle', 0.05, 120);
    }, 440);
  }

  /**
   * Rapid riffle deal texture.
   */
  riffle() {
    if (!this.on) return;
    const taps = 8;
    for (let i = 0; i < taps; i++) {
      setTimeout(() => {
        this.noise(0.025, 0.03, 2400 + i * 150, 'highpass');
      }, i * 28);
    }
  }

  /**
   * Grand fanfare for high-scoring hands (C major / G major arpeggio flourish).
   */
  fanfare20() {
    if (!this.on) return;
    const arpeggio = [
      { f: 523.25, d: 0.14, g: 0.09 },
      { f: 659.25, d: 0.14, g: 0.09 },
      { f: 783.99, d: 0.14, g: 0.10 },
      { f: 1046.50, d: 0.20, g: 0.12 },
      { f: 1318.51, d: 0.22, g: 0.12 },
      { f: 1567.98, d: 0.45, g: 0.14 }
    ];
    arpeggio.forEach((note, idx) => {
      setTimeout(() => {
        this.tone(note.f, note.d, 'triangle', note.g);
        this.tone(note.f * 0.5, note.d * 0.8, 'sine', note.g * 0.5);
      }, idx * 110);
    });
  }

  /**
   * Legendary fanfare for the Holy Grail 29-hand and championship victories.
   * Multi-octave royal brass & bell arpeggio with celebratory final chord.
   */
  fanfare29() {
    if (!this.on) return;
    const royalNotes = [
      { f: 523.25, d: 0.12, g: 0.10 }, // C5
      { f: 659.25, d: 0.12, g: 0.10 }, // E5
      { f: 783.99, d: 0.12, g: 0.11 }, // G5
      { f: 1046.50, d: 0.16, g: 0.13 }, // C6
      { f: 1318.51, d: 0.18, g: 0.13 }, // E6
      { f: 1567.98, d: 0.22, g: 0.14 }, // G6
      { f: 2093.00, d: 0.55, g: 0.15 }  // C7
    ];
    royalNotes.forEach((note, idx) => {
      setTimeout(() => {
        this.tone(note.f, note.d, 'triangle', note.g);
        this.tone(note.f * 0.5, note.d * 0.85, 'sine', note.g * 0.6);
        this.tone(note.f * 1.5, note.d * 0.35, 'sine', note.g * 0.3);
      }, idx * 95);
    });

    // Sustained major chord celebration flourish
    setTimeout(() => {
      this.tone(523.25, 0.9, 'triangle', 0.10);
      this.tone(659.25, 0.9, 'sine', 0.10);
      this.tone(783.99, 0.9, 'triangle', 0.11);
      this.tone(1046.50, 0.95, 'sine', 0.12);
      this.noise(0.4, 0.05, 3800, 'bandpass');
    }, 720);
  }

  /**
   * Rewarding chime for trophy / achievement badge unlocks.
   */
  achievement() {
    if (!this.on) return;
    const notes = [
      { f: 587.33, d: 0.15, g: 0.11 }, // D5
      { f: 739.99, d: 0.15, g: 0.12 }, // F#5
      { f: 880.00, d: 0.18, g: 0.13 }, // A5
      { f: 1174.66, d: 0.45, g: 0.15 } // D6
    ];
    notes.forEach((n, idx) => {
      setTimeout(() => {
        this.tone(n.f, n.d, 'sine', n.g);
        this.tone(n.f * 2, n.d * 0.5, 'triangle', n.g * 0.35);
      }, idx * 80);
    });
  }

  chime15() {
    this.tone(523, 0.18, 'sine', 0.09);
    setTimeout(() => this.tone(659, 0.22, 'sine', 0.08), 90);
  }

  bell31() {
    this.tone(784, 0.35, 'sine', 0.1, 392);
    this.tone(1175, 0.12, 'triangle', 0.05);
  }

  skunk() {
    this.tone(140, 0.55, 'sawtooth', 0.07, 55);
    this.tone(90, 0.7, 'sine', 0.05, 40);
  }

  /**
   * Playful glissando / slide whistle descent for Chutes and Ladders.
   */
  chutesAndLadders() {
    if (!this.on) return;
    this.ensure();
    const slides = [
      { f: 880, d: 0.22, end: 440 },
      { f: 620, d: 0.22, end: 260 },
      { f: 380, d: 0.35, end: 110 }
    ];
    slides.forEach((s, idx) => {
      setTimeout(() => {
        this.tone(s.f, s.d, 'triangle', 0.09, s.end);
        this.tone(s.f * 1.5, s.d * 0.7, 'sine', 0.05, s.end * 1.5);
      }, idx * 160);
    });
    setTimeout(() => {
      this.tone(110, 0.25, 'sine', 0.12, 60);
    }, 520);
  }

  gameHole() {
    this.tone(160, 0.18, 'sine', 0.1);
    this.tone(880, 0.08, 'triangle', 0.06);
  }

  /**
   * Playful comic descending slide for 🦨 Skunked reaction.
   */
  reactionSkunk() {
    if (!this.on) return;
    this.tone(320, 0.45, 'sawtooth', 0.09, 85);
    setTimeout(() => this.tone(210, 0.4, 'sine', 0.08, 65), 100);
  }

  /**
   * Crisp celebratory glasses clink for 🍻 Cheers reaction.
   */
  reactionCheers() {
    if (!this.on) return;
    this.tone(2400, 0.35, 'sine', 0.12);
    this.tone(3180, 0.28, 'triangle', 0.08);
    setTimeout(() => {
      this.tone(2550, 0.32, 'sine', 0.10);
      this.tone(3400, 0.22, 'triangle', 0.07);
    }, 65);
  }

  /**
   * Dramatic gasp/surprise chord for 😱 No Way! reaction.
   */
  reactionNoWay() {
    if (!this.on) return;
    this.tone(440, 0.38, 'sine', 0.11, 880);
    this.tone(660, 0.35, 'triangle', 0.09, 1320);
    setTimeout(() => {
      this.tone(880, 0.3, 'sine', 0.08, 1100);
    }, 70);
  }

  /**
   * Snappy crisp cribbage chime for 🃏 Nice 15 reaction.
   */
  reactionNice15() {
    if (!this.on) return;
    this.tone(1046.5, 0.18, 'sine', 0.12);
    this.tone(2093, 0.12, 'triangle', 0.06);
    setTimeout(() => {
      this.tone(1567.98, 0.25, 'sine', 0.13);
      this.tone(3135.96, 0.15, 'triangle', 0.07);
    }, 85);
  }

  /**
   * Punchy bass boom burst with high crackle for 💥 Boom! reaction.
   */
  reactionBoom() {
    if (!this.on) return;
    this.tone(120, 0.42, 'sine', 0.22, 38);
    this.noise(0.25, 0.14, 2200, 'lowpass');
    setTimeout(() => {
      this.tone(65, 0.35, 'triangle', 0.15, 28);
    }, 45);
  }

  card() {
    this.cardSlide();
  }

  deal() {
    this.cardSlide();
  }

  /**
   * Sound bite: "Fifteen two, fifteen four, and the rest don't score!"
   * Playful rhythmic 4-note ascending cadence.
   */
  banterFifteenTwo() {
    if (!this.on) return;
    const notes = [
      { f: 523.25, d: 0.12, g: 0.11 }, // 15
      { f: 659.25, d: 0.12, g: 0.11 }, // 2
      { f: 783.99, d: 0.14, g: 0.12 }, // 15
      { f: 1046.50, d: 0.28, g: 0.14 } // 4!
    ];
    notes.forEach((n, idx) => {
      setTimeout(() => {
        this.tone(n.f, n.d, 'triangle', n.g);
        this.tone(n.f * 0.5, n.d * 0.8, 'sine', n.g * 0.5);
      }, idx * 115);
    });
  }

  /**
   * Sound bite: "One for his nob!"
   * Snappy Jack chime with celebratory wood thunk.
   */
  banterNob() {
    if (!this.on) return;
    this.thunk(1.2);
    setTimeout(() => {
      this.tone(880, 0.14, 'sine', 0.12);
      this.tone(1320, 0.22, 'triangle', 0.10);
    }, 45);
  }

  /**
   * Sound bite: "Go!"
   * Urgent double-knock wood tap alert.
   */
  banterGo() {
    if (!this.on) return;
    this.thunk(1.1);
    setTimeout(() => this.thunk(1.25), 90);
  }

  /**
   * Sound bite: "Pair for two!"
   * Harmonic pair chime chord.
   */
  banterPair() {
    if (!this.on) return;
    this.tone(659.25, 0.22, 'triangle', 0.10);
    this.tone(987.77, 0.24, 'sine', 0.11);
    setTimeout(() => {
      this.tone(783.99, 0.28, 'sine', 0.12);
      this.tone(1174.66, 0.32, 'triangle', 0.08);
    }, 75);
  }

  /**
   * Sound bite: "Thirty-one for two!"
   * Regal milestone chime.
   */
  banterThirtyOne() {
    if (!this.on) return;
    this.bell31();
  }

  /**
   * Sound bite: "Two for his heels!"
   * Double wood tap and starter bell.
   */
  banterHeels() {
    if (!this.on) return;
    this.thunk(0.95);
    setTimeout(() => this.thunk(1.15), 85);
    setTimeout(() => this.chime15(), 170);
  }

  /**
   * Plays acoustic sound bite and optional synthesized speech if enabled.
   * @param {string} method Audio method name
   * @param {string} [spokenText] Speech text for browser speech synthesis
   */
  playBanterSound(method, spokenText = '') {
    if (!this.on) return;
    if (typeof this[method] === 'function') {
      this[method]();
    }
    // Web Speech API for voice line if available
    if (spokenText && typeof window !== 'undefined' && 'speechSynthesis' in window) {
      try {
        const utter = new SpeechSynthesisUtterance(spokenText);
        utter.rate = 1.05;
        utter.pitch = 1.05;
        utter.volume = 0.75;
        window.speechSynthesis.speak(utter);
      } catch (e) {}
    }
  }

  pop() {
    this.peg();
  }

  alarm(force = false) {
    if (!this.on && !force) return;
    const ctx = this.ensure();
    if (!ctx) return;
    const now = ctx.currentTime;
    for (let i = 0; i < 6; i++) {
      const t = now + i * 0.32;
      const o1 = ctx.createOscillator();
      const g1 = ctx.createGain();
      o1.type = 'sawtooth';
      o1.frequency.setValueAtTime(880, t);
      g1.gain.setValueAtTime(0.22, t);
      g1.gain.exponentialRampToValueAtTime(0.001, t + 0.13);
      o1.connect(g1);
      g1.connect(ctx.destination);
      o1.start(t);
      o1.stop(t + 0.13);

      const o2 = ctx.createOscillator();
      const g2 = ctx.createGain();
      o2.type = 'square';
      o2.frequency.setValueAtTime(1175, t + 0.14);
      g2.gain.setValueAtTime(0.22, t + 0.14);
      g2.gain.exponentialRampToValueAtTime(0.001, t + 0.28);
      o2.connect(g2);
      g2.connect(ctx.destination);
      o2.start(t + 0.14);
      o2.stop(t + 0.28);
    }
  }

  /**
   * Heavy metallic iron prison cell slam down sound with ringing dissonance.
   */
  jailClang() {
    if (!this.on) return;
    // Heavy low iron thump
    this.tone(90, 0.45, 'sawtooth', 0.25, 30);
    this.noise(0.2, 0.18, 900, 'lowpass');
    // Resonant metallic bars ringing
    setTimeout(() => {
      this.tone(480, 0.6, 'triangle', 0.14, 460);
      this.tone(720, 0.5, 'sine', 0.12, 700);
      this.tone(1150, 0.35, 'triangle', 0.08);
      this.noise(0.12, 0.06, 2400, 'bandpass');
    }, 25);
  }

  /**
   * Snappy mechanical camera shutter click and flash capacitor pop.
   */
  cameraShutter() {
    if (!this.on) return;
    // Mirror slap / shutter opening
    this.noise(0.025, 0.18, 3800, 'highpass');
    this.tone(1200, 0.02, 'triangle', 0.1);
    // Shutter curtain closing & winder gear click
    setTimeout(() => {
      this.noise(0.035, 0.22, 2800, 'bandpass');
      this.tone(850, 0.03, 'sine', 0.12);
    }, 45);
    setTimeout(() => {
      this.noise(0.015, 0.08, 4200, 'highpass');
    }, 90);
  }

  /**
   * Wooden saloon swinging doors kick open with creaking hinges.
   */
  saloonDoorKick() {
    if (!this.on) return;
    // Solid wooden door impact
    this.tone(85, 0.28, 'triangle', 0.24, 45);
    this.noise(0.12, 0.2, 1100, 'bandpass');
    // Creaking wood hinge swing
    setTimeout(() => {
      this.tone(260, 0.35, 'sawtooth', 0.12, 170);
      this.noise(0.08, 0.14, 1800, 'bandpass');
    }, 40);
    // Secondary door bounce back
    setTimeout(() => {
      this.tone(120, 0.2, 'triangle', 0.12, 70);
    }, 380);
  }

  /**
   * Heavy glass beer stein sliding across wooden bar counter.
   */
  barMugSlide() {
    if (!this.on) return;
    this.noise(0.55, 0.2, 1350, 'bandpass');
    this.tone(145, 0.45, 'sine', 0.12, 115);
  }

  /**
   * Resonant clink/bump of thick glass beer mug on counter or card.
   */
  glassClink() {
    if (!this.on) return;
    this.tone(1880, 0.4, 'sine', 0.22);
    this.tone(2450, 0.35, 'sine', 0.15);
    this.tone(950, 0.2, 'triangle', 0.14, 400);
    this.noise(0.04, 0.12, 4500, 'highpass');
  }

  /**
   * Rapid rhythmic beer gulping / drinking glug sounds.
   */
  beerGulp() {
    if (!this.on) return;
    const gulps = [
      { t: 0, f1: 190, f2: 270 },
      { t: 180, f1: 220, f2: 310 },
      { t: 360, f1: 250, f2: 350 },
      { t: 540, f1: 280, f2: 390 }
    ];
    gulps.forEach(g => {
      setTimeout(() => {
        this.tone(g.f1, 0.12, 'sine', 0.18, g.f2);
        this.tone(g.f1 * 0.5, 0.08, 'triangle', 0.1);
      }, g.t);
    });
  }

  /**
   * Comical drunken upward pitch-swept hiccup.
   */
  drunkenHiccup() {
    if (!this.on) return;
    this.tone(340, 0.09, 'sine', 0.25, 740);
    setTimeout(() => {
      this.tone(680, 0.06, 'triangle', 0.15, 880);
    }, 20);
  }

  /**
   * Stumble and collapse face-first onto wooden bar stool thud.
   */
  stoolCollapseThud() {
    if (!this.on) return;
    // Wood frame clatter
    this.noise(0.15, 0.22, 1400, 'bandpass');
    this.tone(180, 0.2, 'sawtooth', 0.15, 90);
    // Heavy deep faceplant thud
    setTimeout(() => {
      this.tone(70, 0.45, 'triangle', 0.32, 35);
      this.noise(0.22, 0.25, 800, 'lowpass');
    }, 45);
  }

  /**
   * Crisp, playful popping sound for floating beer bubbles.
   */
  bubblePop() {
    if (!this.on) return;
    this.tone(1100, 0.035, 'sine', 0.16, 1750);
  }

  /**
   * Heavy spoked iron/wooden wheels squeaking and rolling into position.
   */
  cannonRollWheel() {
    if (!this.on) return;
    this.noise(0.65, 0.16, 500, 'lowpass');
    this.tone(90, 0.55, 'triangle', 0.14, 75);
    // Squeak chirps
    setTimeout(() => this.tone(980, 0.08, 'sine', 0.08, 1250), 120);
    setTimeout(() => this.tone(1120, 0.09, 'sine', 0.07, 850), 340);
  }

  /**
   * Burning gunpowder fuse sizzling with bright sparks.
   */
  cannonFuseHiss() {
    if (!this.on) return;
    this.noise(1.1, 0.22, 3800, 'highpass');
    // Rhythmic spark pops
    [80, 220, 390, 580, 780, 960].forEach(d => {
      setTimeout(() => {
        if (!this.on) return;
        this.tone(2200 + Math.random() * 800, 0.03, 'sine', 0.14);
      }, d);
    });
  }

  /**
   * Massive concussive cartoon ACME cannon explosion blast.
   */
  cannonKaboom() {
    if (!this.on) return;
    // Initial sharp gunshot crack
    this.noise(0.85, 0.45, 1200, 'bandpass');
    this.tone(320, 0.15, 'sawtooth', 0.45, 60);
    // Deep heavy sub-bass boom
    this.tone(95, 0.9, 'triangle', 0.55, 28);
    setTimeout(() => {
      if (!this.on) return;
      this.noise(0.9, 0.32, 280, 'lowpass');
      this.tone(55, 0.7, 'sine', 0.4, 20);
    }, 60);
  }

  /**
   * Cartoon Doppler whistle of object streaking through the sky.
   */
  cannonFlightWhistle() {
    if (!this.on) return;
    this.tone(1450, 0.75, 'sine', 0.22, 450);
    this.tone(725, 0.7, 'triangle', 0.12, 225);
  }

  /**
   * Springy, punchy lawn-dart lawn/ground impact thud with vibrating boing.
   */
  lawnDartThud() {
    if (!this.on) return;
    // Sharp high-velocity whack
    this.noise(0.12, 0.38, 2200, 'bandpass');
    this.tone(260, 0.18, 'sawtooth', 0.35, 65);
    // Deep ground thud
    this.tone(68, 0.42, 'triangle', 0.4, 30);
    // Springy cartoon vibration boing
    setTimeout(() => {
      if (!this.on) return;
      this.tone(420, 0.35, 'sine', 0.25, 210);
      this.tone(840, 0.25, 'triangle', 0.15, 420);
    }, 40);
  }

  /**
   * Twinkling cartoon stars circling around the head of a stunned card.
   */
  dizzyStarsChime() {
    if (!this.on) return;
    const starNotes = [1318.51, 1567.98, 1760.00, 2093.00, 2349.32, 2637.02];
    starNotes.forEach((f, idx) => {
      setTimeout(() => {
        if (!this.on) return;
        this.tone(f, 0.22, 'sine', 0.15);
        this.tone(f * 2, 0.14, 'triangle', 0.08);
      }, idx * 110);
    });
  }

  /**
   * Oscillating two-tone police cruiser emergency siren wail.
   */
  policeSirenWail() {
    if (!this.on) return;
    const sirenFrequencies = [640, 920, 640, 920, 640, 920];
    sirenFrequencies.forEach((f, idx) => {
      setTimeout(() => {
        if (!this.on) return;
        const target = sirenFrequencies[Math.min(idx + 1, sirenFrequencies.length - 1)];
        this.tone(f, 0.28, 'sawtooth', 0.22, target);
        this.tone(f * 0.5, 0.25, 'triangle', 0.15, target * 0.5);
      }, idx * 220);
    });
  }

  /**
   * Revving motorcycle engine followed by high-pitched tire skid screech.
   */
  motorcycleRev() {
    if (!this.on) return;
    // Throttle engine rev (low saw pitch-up)
    this.tone(90, 0.35, 'sawtooth', 0.28, 220);
    this.noise(0.3, 0.15, 800, 'lowpass');
    setTimeout(() => {
      if (!this.on) return;
      this.tone(180, 0.3, 'sawtooth', 0.32, 280);
      // Hard tire skid screech
      setTimeout(() => {
        if (!this.on) return;
        this.noise(0.45, 0.3, 3600, 'bandpass');
        this.tone(1450, 0.35, 'sine', 0.18, 980);
      }, 150);
    }, 200);
  }

  /**
   * Piercing police referee whistle with rapid flutter vibrato.
   */
  policeWhistle() {
    if (!this.on) return;
    const chirps = [
      { t: 0, dur: 0.18 },
      { t: 220, dur: 0.35 }
    ];
    chirps.forEach(c => {
      setTimeout(() => {
        if (!this.on) return;
        this.tone(2620, c.dur, 'sine', 0.28, 2750);
        this.tone(2950, c.dur, 'triangle', 0.22, 3080);
        this.noise(c.dur, 0.14, 5200, 'bandpass');
      }, c.t);
    });
  }

  /**
   * Crisp, loud paper citation slap onto card face with cartoon impact thud.
   */
  ticketSlap() {
    if (!this.on) return;
    // Sharp high transient paper slap
    this.noise(0.08, 0.42, 4200, 'highpass');
    this.tone(580, 0.08, 'sawtooth', 0.38, 140);
    // Heavy rubber-stamp impact
    setTimeout(() => {
      if (!this.on) return;
      this.tone(120, 0.22, 'triangle', 0.35, 45);
      this.noise(0.12, 0.25, 950, 'lowpass');
    }, 20);
  }

  /**
   * Futuristic GPS prompt chime followed by static recalculating glitch.
   */
  gpsChimeRecalculate() {
    if (!this.on) return;
    // 3-tone pleasant navigation prompt: E5 (659Hz) -> G#5 (830Hz) -> B5 (987Hz)
    const notes = [659.25, 830.61, 987.77];
    notes.forEach((f, idx) => {
      setTimeout(() => {
        if (!this.on) return;
        this.tone(f, 0.12, 'sine', 0.25);
      }, idx * 110);
    });
    // Glitchy recalculation warble
    setTimeout(() => {
      if (!this.on) return;
      this.tone(740, 0.18, 'sawtooth', 0.18, 480);
      this.noise(0.15, 0.12, 2800, 'bandpass');
    }, 380);
  }

  /**
   * Whirling rotary whoosh, tire squeal, and comical dizzy carnival slide whistle.
   */
  rotarySpin() {
    if (!this.on) return;
    // Circling Doppler hum (sawtooth with rising/falling sweep)
    this.tone(140, 0.45, 'sawtooth', 0.22, 280);
    setTimeout(() => {
      if (!this.on) return;
      this.tone(280, 0.45, 'sawtooth', 0.24, 160);
      // Hard centrifugal tire screech
      this.noise(0.35, 0.26, 3400, 'bandpass');
      this.tone(1350, 0.3, 'sine', 0.16, 920);
    }, 400);
    // Dizzy slide whistle
    setTimeout(() => {
      if (!this.on) return;
      this.tone(880, 0.4, 'sine', 0.2, 1760);
    }, 800);
  }

  /**
   * Bumpy dirt road suspension rattle, wooden plank clatter, and vintage ahooga horn.
   */
  bumpyDetourRattle() {
    if (!this.on) return;
    // Rapid suspension bumps & gravel rattling
    const bumps = [0, 80, 160, 240, 320, 420];
    bumps.forEach(t => {
      setTimeout(() => {
        if (!this.on) return;
        this.tone(90 + Math.random() * 40, 0.08, 'triangle', 0.28, 45);
        this.noise(0.06, 0.18, 1200, 'lowpass');
      }, t);
    });
    // Vintage "AHOOGA" horn at ~550ms
    setTimeout(() => {
      if (!this.on) return;
      // "A-" (low saw pitch-up)
      this.tone(220, 0.14, 'sawtooth', 0.32, 440);
      setTimeout(() => {
        if (!this.on) return;
        // "-HOO-" (high pitch sustain)
        this.tone(440, 0.22, 'sawtooth', 0.35, 415);
        setTimeout(() => {
          if (!this.on) return;
          // "-GA!" (drop down)
          this.tone(370, 0.18, 'sawtooth', 0.3, 196);
        }, 200);
      }, 130);
    }, 550);
  }

  /**
   * Dramatic brassy fanfare when a Spade is cut in Spades mode.
   */
  spades() {
    if (!this.on) return;
    this.tone(293.66, 0.18, 'sawtooth', 0.22); // D4
    setTimeout(() => {
      if (!this.on) return;
      this.tone(369.99, 0.18, 'sawtooth', 0.24); // F#4
    }, 120);
    setTimeout(() => {
      if (!this.on) return;
      this.tone(440.00, 0.22, 'sawtooth', 0.26); // A4
    }, 240);
    setTimeout(() => {
      if (!this.on) return;
      this.tone(587.33, 0.45, 'sawtooth', 0.32); // D5
      this.tone(880.00, 0.40, 'triangle', 0.18); // A5 sparkle
    }, 380);
  }

  setOn(enabled) {
    const s = this.storage || (typeof window !== 'undefined' && window.appStorage) || appStorage || (typeof localStorage !== 'undefined' ? localStorage : null);
    if (s) {
      try {
        s.setItem('crazyCribbage.sfx', this.on ? 'on' : 'off');
      } catch (e) {}
    }
  }

  toggle() {
    this.setOn(!this.on);
    return this.on;
  }
}
